import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { authService } from '../services/authService';
import { config } from '../config/environment';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  useTheme,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  School,
  FamilyRestroom,
  Psychology,
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  ArrowForward,
  ArrowBack,
  Google,
  Facebook,
  Apple,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

interface UserType {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const CODE_EXPIRY_SECONDS = 15 * 60;

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const googleClientId = config.services.googleClientId;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authFailed = urlParams.get('authFailed') === 'true' || location.state?.authFailed;

    if (authFailed) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('userLogout'));
      window.history.replaceState({}, '', '/login');
      return;
    }

    const token = authService.getToken();
    const user = authService.getCurrentUser();

    if (token && user) {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        if (user.userType === 'PARENT') navigate('/parent-dashboard', { replace: true });
        else if (user.userType === 'STUDENT') navigate('/student-dashboard', { replace: true });
        else if (user.userType === 'TEACHER') navigate('/teacher-dashboard', { replace: true });
        else if (user.userType === 'TUTOR') navigate('/tutor-dashboard', { replace: true });
        else if (user.userType === 'SYSTEM_ADMIN') navigate('/admin/login', { replace: true });
        else navigate('/', { replace: true });
      } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
  }, [navigate, location]);

  const [formData, setFormData] = useState({
    userType: 'STUDENT',
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Code-login state
  const [loginMode, setLoginMode] = useState<'password' | 'code'>('password');
  const [codeStep, setCodeStep] = useState<'email' | 'verify'>('email');
  const [loginCode, setLoginCode] = useState('');
  const [codeSecondsLeft, setCodeSecondsLeft] = useState(0);
  const [codeSentMessage, setCodeSentMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCodeSecondsLeft(CODE_EXPIRY_SECONDS);
    timerRef.current = setInterval(() => {
      setCodeSecondsLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const userTypes: UserType[] = [
    { value: 'STUDENT', label: 'Student', description: 'Access your learning progress and courses', icon: <Person sx={{ fontSize: 24 }} /> },
    { value: 'PARENT', label: 'Parent', description: "Monitor your child's educational journey", icon: <FamilyRestroom sx={{ fontSize: 24 }} /> },
    { value: 'TEACHER', label: 'Teacher', description: 'Manage your classes and student progress', icon: <School sx={{ fontSize: 24 }} /> },
    { value: 'TUTOR', label: 'Tutor', description: 'Provide personalized tutoring and guidance', icon: <Psychology sx={{ fontSize: 24 }} /> },
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const redirectAfterLogin = (loginResponse: { user: { userType: string }; requiresActivation?: boolean; roleToActivate?: string }) => {
    window.dispatchEvent(new Event('userLogin'));

    if (loginResponse.requiresActivation && loginResponse.roleToActivate) {
      navigate(`/role-activation?role=${loginResponse.roleToActivate}`, { replace: true });
      return;
    }

    const from = location.state?.from || '/';
    const t = loginResponse.user.userType;
    if (from.includes('/parent-') && t !== 'PARENT') {
      navigate(t === 'STUDENT' ? '/student-dashboard' : t === 'TEACHER' ? '/teacher-dashboard' : '/', { replace: true });
    } else if (from.includes('/student-') && t !== 'STUDENT') {
      navigate(t === 'PARENT' ? '/parent-dashboard' : t === 'TEACHER' ? '/teacher-dashboard' : '/', { replace: true });
    } else if (from.includes('/teacher-') && t !== 'TEACHER') {
      navigate(t === 'PARENT' ? '/parent-dashboard' : t === 'STUDENT' ? '/student-dashboard' : '/', { replace: true });
    } else if (from !== '/' && from !== '/login') {
      navigate(from, { replace: true });
    } else {
      const routes: Record<string, string> = {
        PARENT: '/parent-dashboard', STUDENT: '/student-dashboard',
        TEACHER: '/teacher-dashboard', TUTOR: '/tutor-dashboard', SYSTEM_ADMIN: '/admin/login',
      };
      navigate(routes[t] || '/', { replace: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setLoginError(null);
    try {
      const loginResponse = await authService.login({
        email: formData.email,
        password: formData.password,
        userType: formData.userType,
      });
      redirectAfterLogin(loginResponse);
    } catch (err: any) {
      const status = err.response?.status;
      const message = err.response?.data?.message ?? err.message ?? 'Network error. Please check if backend is running.';
      if (status === 403 || /verify your email|verification link|resend verification/i.test(String(message))) {
        navigate('/please-verify-email', { state: { email: formData.email }, replace: true });
        return;
      }
      setLoginError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    setLoginError(null);
    setCodeSentMessage(null);
    try {
      await authService.requestLoginCode(formData.email, formData.userType);
      setCodeStep('verify');
      setLoginCode('');
      startCountdown();
      setCodeSentMessage(`A 6-digit code has been sent to ${formData.email}`);
    } catch (err: any) {
      const status = err.response?.status;
      const message = err.message || 'Failed to send login code.';
      if (status === 403 || /verify your email|verification link|resend verification/i.test(message)) {
        navigate('/please-verify-email', { state: { email: formData.email }, replace: true });
        return;
      }
      setLoginError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loginCode.length !== 6 || !/^\d{6}$/.test(loginCode)) {
      setErrors({ code: 'Please enter a valid 6-digit code' });
      return;
    }
    setLoading(true);
    setLoginError(null);
    try {
      const loginResponse = await authService.verifyLoginCode(formData.email, loginCode, formData.userType);
      if (timerRef.current) clearInterval(timerRef.current);
      redirectAfterLogin(loginResponse);
    } catch (err: any) {
      const message = err.message || 'Code verification failed.';
      setLoginError(message);
      if (/invalid login code/i.test(message)) {
        setCodeSentMessage(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setLoginError(null);
    setCodeSentMessage(null);
    try {
      await authService.requestLoginCode(formData.email, formData.userType);
      setLoginCode('');
      startCountdown();
      setCodeSentMessage('A new code has been sent to your email.');
    } catch (err: any) {
      setLoginError(err.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  const switchToCodeMode = () => {
    setLoginMode('code');
    setCodeStep('email');
    setLoginCode('');
    setLoginError(null);
    setCodeSentMessage(null);
    setErrors({});
    if (timerRef.current) clearInterval(timerRef.current);
    setCodeSecondsLeft(0);
  };

  const switchToPasswordMode = () => {
    setLoginMode('password');
    setLoginError(null);
    setCodeSentMessage(null);
    setErrors({});
    if (timerRef.current) clearInterval(timerRef.current);
    setCodeSecondsLeft(0);
  };

  const handleGoogleSuccess = async (credential: string) => {
    setLoading(true);
    setLoginError(null);
    try {
      const loginResponse = await authService.loginWithGoogle(credential);
      redirectAfterLogin(loginResponse);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google sign-in failed.';
      setLoginError(message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            Welcome Back
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Sign in to continue your learning journey
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 2, md: 4 } }}>
          <CardContent>
            {/* User Type Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                I am a...
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                {userTypes.map((type) => (
                  <Button
                    key={type.value}
                    variant={formData.userType === type.value ? 'contained' : 'outlined'}
                    onClick={() => handleInputChange('userType', type.value)}
                    startIcon={
                      React.cloneElement(type.icon as React.ReactElement<any>, {
                        sx: { fontSize: 24, color: formData.userType === type.value ? 'white' : 'primary.main' }
                      })
                    }
                    sx={{ minWidth: 'auto', px: 2, py: 1, borderRadius: 2, textTransform: 'none', fontSize: '0.875rem' }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Error / Info Display */}
            {loginError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {loginError}
                {loginMode === 'password' && /password is not configured/i.test(loginError) && (
                  <>
                    {' '}
                    <Typography
                      component="span"
                      sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, color: 'primary.main' }}
                      onClick={switchToCodeMode}
                    >
                      Request a sign-in code on email
                    </Typography>
                  </>
                )}
                {loginMode === 'code' && codeStep === 'verify' && /invalid login code/i.test(loginError) && (
                  <>
                    {' '}
                    <Typography
                      component="span"
                      sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, color: 'primary.main' }}
                      onClick={handleResendCode}
                    >
                      Resend code
                    </Typography>
                  </>
                )}
              </Alert>
            )}
            {codeSentMessage && !loginError && (
              <Alert severity="success" sx={{ mb: 3 }}>{codeSentMessage}</Alert>
            )}

            {/* ── Password Login Form ── */}
            {loginMode === 'password' && (
              <>
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Email sx={{ color: 'text.secondary' }} /></InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Lock sx={{ color: 'text.secondary' }} /></InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.rememberMe}
                          onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                        />
                      }
                      label="Remember me"
                    />
                    <Typography
                      component={Link}
                      to="/forgot-password"
                      sx={{
                        color: 'primary.main',
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Forgot password?
                    </Typography>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                    disabled={loading}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </Box>

                <Button
                  fullWidth
                  variant="text"
                  onClick={switchToCodeMode}
                  sx={{ mb: 3, textTransform: 'none', color: 'primary.main' }}
                >
                  Sign in with email code instead
                </Button>
              </>
            )}

            {/* ── Code Login Flow ── */}
            {loginMode === 'code' && codeStep === 'email' && (
              <>
                <Box component="form" onSubmit={handleRequestCode}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    We'll send a 6-digit login code to your email
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Email sx={{ color: 'text.secondary' }} /></InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                    disabled={loading}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Sending Code...' : 'Send Login Code'}
                  </Button>
                </Box>

                <Button
                  fullWidth
                  variant="text"
                  startIcon={<ArrowBack />}
                  onClick={switchToPasswordMode}
                  sx={{ mb: 3, textTransform: 'none', color: 'text.secondary' }}
                >
                  Back to password sign in
                </Button>
              </>
            )}

            {loginMode === 'code' && codeStep === 'verify' && (
              <>
                <Box component="form" onSubmit={handleVerifyCode}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                    Enter the 6-digit code sent to
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
                    {formData.email}
                  </Typography>

                  <TextField
                    fullWidth
                    label="Login Code"
                    value={loginCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setLoginCode(v);
                      if (errors.code) setErrors(prev => ({ ...prev, code: '' }));
                    }}
                    error={!!errors.code}
                    helperText={errors.code}
                    inputProps={{ maxLength: 6, inputMode: 'numeric', style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem', fontFamily: 'monospace' } }}
                    sx={{ mb: 2 }}
                  />

                  {codeSecondsLeft > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Code expires in</Typography>
                        <Typography variant="caption" color={codeSecondsLeft < 60 ? 'error.main' : 'text.secondary'} fontWeight={600}>
                          {formatTime(codeSecondsLeft)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(codeSecondsLeft / CODE_EXPIRY_SECONDS) * 100}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  )}

                  {codeSecondsLeft === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Code has expired.{' '}
                      <Typography
                        component="span"
                        sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, color: 'primary.main' }}
                        onClick={handleResendCode}
                      >
                        Send a new code
                      </Typography>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                    disabled={loading || loginCode.length !== 6 || codeSecondsLeft === 0}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ArrowBack />}
                    onClick={switchToPasswordMode}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Password sign in
                  </Button>
                  {(codeSecondsLeft > 0 || /invalid login code/i.test(loginError || '')) && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleResendCode}
                      disabled={loading}
                      sx={{ textTransform: 'none' }}
                    >
                      Resend code
                    </Button>
                  )}
                </Box>
              </>
            )}

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                or continue with
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Social Login: Google */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: 'column', alignItems: 'center' }}>
              {googleClientId ? (
                <GoogleLogin
                  onSuccess={(res) => {
                    if (res.credential) handleGoogleSuccess(res.credential);
                  }}
                  onError={() => setLoginError('Google sign-in was cancelled or failed.')}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                  width="320"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sign in with Google: set VITE_GOOGLE_CLIENT_ID in your environment to enable.
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, width: '100%', maxWidth: 320 }}>
                <Button fullWidth variant="outlined" disabled startIcon={<Facebook />} sx={{ py: 1.5 }}>
                  Facebook
                </Button>
                <Button fullWidth variant="outlined" disabled startIcon={<Apple />} sx={{ py: 1.5 }}>
                  Apple
                </Button>
              </Box>
            </Box>

            {/* Sign Up Link */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Don't have an account?{' '}
                <Link to="/signup" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>
                  Sign up here
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            By signing in, you agree to our{' '}
            <Link to="/terms-of-service" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
